// PropertyPath — Runtime-generated documentation (User Manual + Glossary).
//
// These replace the static public/docs/*.docx binaries, which were orphaned
// artifacts: no generator script existed anywhere in the repo, they hardcoded
// "Vacation Home Group" and the house palette, and they were frozen at app
// v3.7.4 while the app shipped 4.x. Every agent on the platform was handing
// their clients a document branded to somebody else.
//
// Built at click time from the live brand payload, exactly like the PDF report
// in Dashboard.js. Same approach already shipped in RentOrOwn v2.9.1.
import { getBrandPayload } from './brandState';
import { resolveReportPalette } from './reportPalette';

export const DOC_VERSION = '4.5.1';

// HTML-escape anything sourced from the brand payload. These values are
// agent-supplied and land inside markup; an unescaped angle bracket would
// break the document.
function esc(v) {
  return String(v == null ? '' : v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Resolve the agent brand fresh at call time. The payload loads async, so a
// value captured at module scope would always be the house default.
function docBrand() {
  const bp = (typeof getBrandPayload === 'function' ? getBrandPayload() : null) || {};
  const bi = bp.brand_identity || {};
  const br = bp.brokerage_identity || {};
  const ap = bp.agent_profile || null;
  const { gold, accent } = resolveReportPalette(bi);
  // A payload that names ANY party is "branded". House fallbacks apply only
  // when nothing is slug-fed at all — otherwise an agent at another brokerage
  // gets our legal name and our website stamped on THEIR client's document.
  const branded = !!(bi.name || br.legal_name || (ap && ap.name) || bi.website_url);
  const siteRaw = bi.website_url || '';
  const site = siteRaw.replace(/^https?:\/\//, '').replace(/\/+$/, '')
    || (branded ? '' : 'vacationhomegroup.net');
  return {
    gold,
    accent,
    name: bi.name || br.legal_name || (ap && ap.name) || 'Vacation Home Group',
    legal: br.legal_name || (branded ? '' : 'Real Broker NH, LLC'),
    site,
    siteFull: siteRaw || (site ? 'https://www.' + site : ''),
    logo: bi.logo_url || '',
    agentName: (ap && ap.name) || '',
    agentPhone: (ap && ap.phone) || br.phone || '',
    agentEmail: (ap && ap.email) || '',
    // Slug-fed only. Blank when the brokerage supplies none — never invented.
    disclosure: br.disclosure || '',
  };
}

function DOC_CSS(G, A) {
  return `
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'Inter',-apple-system,sans-serif;background:#F5F5F5;color:#0A0A0A;font-size:14px;line-height:1.7;}
  .page{max-width:860px;margin:0 auto;padding:48px 40px 80px;}
  .doc-header{background:linear-gradient(135deg,#0B1120,#151D2E);border-bottom:3px solid ${G};padding:32px 40px 28px;margin:-48px -40px 40px;}
  .doc-logo{font-family:'Inter',sans-serif;font-size:22px;font-weight:800;letter-spacing:-.5px;margin-bottom:4px;color:#F8FAFC;}
  .doc-logo img{max-height:38px;display:block;}
  .doc-brand{font-family:Georgia,serif;font-size:14px;font-weight:700;color:${G};letter-spacing:.5px;font-style:italic;}
  h1{font-family:'Playfair Display',Georgia,serif;font-size:42px;font-weight:700;color:#0A0A0A;line-height:1.05;margin:32px 0 8px;}
  h1 span{color:${G};}
  .subtitle{font-family:'JetBrains Mono',monospace;font-size:12px;color:#6B7280;margin-bottom:40px;line-height:1.7;}
  h2{font-family:'Inter',sans-serif;font-size:18px;font-weight:800;color:#0A0A0A;margin:40px 0 12px;padding-bottom:8px;}
  h2::after{content:'';display:block;height:2px;background:linear-gradient(to right,${G},transparent);margin-top:8px;}
  h3{font-family:'Inter',sans-serif;font-size:15px;font-weight:700;color:#0A0A0A;margin:24px 0 8px;}
  p{font-family:'Inter',sans-serif;font-size:13px;color:#334155;line-height:1.8;margin-bottom:12px;}
  ul{margin:0 0 14px 18px;} li{font-size:13px;color:#334155;line-height:1.8;margin-bottom:5px;}
  li strong{color:#0A0A0A;}
  .callout{background:${G}14;border-left:4px solid ${G};border-radius:0 6px 6px 0;padding:12px 16px;margin:16px 0;font-size:13px;color:#334155;line-height:1.7;}
  .callout strong{color:${G};}
  .callout.green{background:${A}14;border-left-color:${A};}
  .callout.green strong{color:${A};}
  table{width:100%;border-collapse:collapse;margin:16px 0;font-size:12px;}
  thead th{background:#0B1120;color:#F8FAFC;padding:10px 12px;text-align:left;font-family:'JetBrains Mono',monospace;font-weight:700;letter-spacing:.08em;font-size:10px;}
  tbody td{padding:9px 12px;border-bottom:1px solid #E2E2E2;color:#334155;vertical-align:top;}
  tbody td:first-child{font-weight:700;color:#0A0A0A;width:190px;font-family:'JetBrains Mono',monospace;font-size:11px;}
  tbody tr:nth-child(even) td{background:#FAFAFA;}
  .contact{background:#FFFFFF;border:1px solid #D8D8D8;border-radius:10px;padding:18px 22px;margin:28px 0 0;text-align:center;}
  .contact .nm{font-family:'Inter',sans-serif;font-size:15px;font-weight:700;color:#0A0A0A;}
  .contact .ln{font-size:12px;color:#6B7280;margin-top:3px;}
  .contact a{color:${G};text-decoration:none;}
  footer.doc-footer{margin-top:40px;padding-top:20px;border-top:1px solid #D8D8D8;font-family:'JetBrains Mono',monospace;font-size:11px;color:#94A3B8;text-align:center;line-height:1.9;}
  @media print{body{background:#fff;} .page{padding:20px;} .doc-header{margin:-20px -20px 28px;}}
`;
}

// Shared document chrome. Every brand-variable value passes through esc().
function docShell(b, title, h1a, h1b, subtitle, body) {
  const logo = b.logo
    ? `<img src="${esc(b.logo)}" alt="${esc(b.name)}">`
    : 'PropertyPath';
  const contact = (b.agentName || b.agentPhone || b.agentEmail) ? `
<div class="contact">
  ${b.agentName ? `<div class="nm">${esc(b.agentName)}</div>` : ''}
  ${b.agentPhone ? `<div class="ln">${esc(b.agentPhone)}</div>` : ''}
  ${b.agentEmail ? `<div class="ln"><a href="mailto:${esc(b.agentEmail)}">${esc(b.agentEmail)}</a></div>` : ''}
  ${b.site ? `<div class="ln"><a href="${esc(b.siteFull)}">${esc(b.site)}</a></div>` : ''}
</div>` : '';
  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>PropertyPath — ${esc(title)}</title>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;600;700&family=Playfair+Display:wght@700&display=swap" rel="stylesheet">
<style>${DOC_CSS(b.gold, b.accent)}</style></head><body><div class="page">
<div class="doc-header">
  <div class="doc-logo">${logo}</div>
  <div class="doc-brand">by ${esc(b.name)}</div>
</div>
<h1>${esc(h1a)} <span>${esc(h1b)}</span></h1>
<p class="subtitle">${esc(subtitle)} · v${DOC_VERSION} · ${esc(b.name)}${b.site ? ' · ' + esc(b.site) : ''}</p>
${body}
${contact}
<footer class="doc-footer">
PropertyPath v${DOC_VERSION} · ${esc(b.name)}${b.legal ? ' · ' + esc(b.legal) : ''}<br>
${b.disclosure ? esc(b.disclosure) + '<br>' : ''}
This tool does not constitute financial, tax, or investment advice. Projections are estimates based on user-provided inputs. Consult a qualified real estate professional, CPA, or financial advisor before making investment decisions.
</footer>
</div></body></html>`;
}

function openInTab(html) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

function triggerDownload(html, filename) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function openDoc(which) {
  openInTab(which === 'glossary' ? buildGlossaryHTML() : buildManualHTML());
}

export function downloadDoc(which) {
  triggerDownload(
    which === 'glossary' ? buildGlossaryHTML() : buildManualHTML(),
    which === 'glossary' ? 'PropertyPath-Glossary.html' : 'PropertyPath-User-Manual.html'
  );
}

export function buildManualHTML() {
  const b = docBrand();
  const body = `
<h2>Getting Started</h2>
<p>PropertyPath is a web-based real estate investment advisor by ${esc(b.name)}. It starts with a personal goal discovery, then models scenarios and recommends the best path. All calculations run in your browser. The theme auto-detects your system preference (dark/light) on first visit, and can be toggled manually.</p>

<h2>Navigation</h2>
<ul>
<li><strong>Calculator:</strong> opens the goal discovery (or questionnaire if already completed)</li>
<li><strong>Resources:</strong> scrolls to documentation</li>
<li><strong>Standard / Pro:</strong> scrolls to the pricing table</li>
<li><strong>Theme toggle:</strong> dark/light mode (auto-detects system preference on first visit)</li>
</ul>

<h2>Onboarding Tour</h2>
<p>First-time users see a 4-step guided tour covering the Overview tab, Assumptions Sliders, Tabs, and Save &amp; Share actions. Click "Next" to step through or "Skip Tour" to dismiss permanently.</p>

<h2>Goal Discovery</h2>
<p>Before entering property details, you answer 5 quick questions about your situation, priority, risk tolerance, timeline, and experience level. Each question has preset options plus a "Something else..." free-text input. Your answers personalize the AI analysis.</p>
<div class="callout"><strong>Buyer Mode:</strong> If you select "I'm evaluating a new purchase," the app switches to Buyer Mode. The questionnaire, dashboard labels, calculations, and AI prompt all adapt for a buy/don't-buy comparison.</div>

<h2>Step 1: Property and Portfolio</h2>
<ul>
<li>Property Type, Location, Purchase Price, Current Value</li>
<li><strong>Years Owned:</strong> auto-calculates mortgage years remaining (hidden in Buyer Mode)</li>
<li>Annual Gross Rent, Management Style</li>
</ul>
<p>In Buyer Mode: "Purchase Price" becomes "Asking Price," "Current Value" becomes "Est. Market Value," and tips reference expected rather than historical data.</p>

<h2>Step 2: Financial Snapshot</h2>
<ul>
<li>Annual Expenses, Vacancy Rate</li>
<li>Mortgage Balance (or Loan Amount in Buyer Mode), Interest Rate, Years Remaining (or Loan Term)</li>
<li><strong>Property Condition:</strong> roof, HVAC, and water heater ages — triggers capital expense projections</li>
<li>Federal Tax Bracket</li>
</ul>
<p>Dollar fields auto-format with commas.</p>

<h2>Step 3: Market and Exit Strategy</h2>
<ul>
<li>Appreciation (0.5% increments), Cap Rate (optional)</li>
<li>Alternative Investment and Expected Return (0.5% increments)</li>
<li>Selling Costs (default 7.5%) or Purchase Costs (default 3%) in Buyer Mode</li>
<li>Exit Strategy with optional 1031 replacement property details (hidden in Buyer Mode)</li>
</ul>

<h2>Dashboard Overview</h2>
<p><strong>Primary metrics:</strong> Hold Total (or Buy &amp; Hold), Sell &amp; Invest (or Don't Buy, Invest), 1031 Exchange, Recommendation.</p>
<p><strong>Secondary metrics:</strong> Calculated Cap Rate, Year 1 Cash Flow, Selling/Purchase Costs.</p>
<p><strong>Upcoming Capital Expenses:</strong> cards showing projected roof, HVAC, and water heater replacements with year, cost, and age. Components past their lifespan show as "Overdue now."</p>
<ul>
<li>Cumulative Wealth Chart</li>
<li>Scenario Radar</li>
<li>Email My Results button (Pro users): sends a branded analysis email</li>
<li>Save Property button: saves the current analysis (up to 3) for comparison</li>
</ul>

<h2>Saved Properties Tab</h2>
<p>Appears when you have 1 or more saved properties. Shows each property as a card with location, date, Hold/Sell totals, and a delete button. With 2 or more properties, shows a grouped bar chart comparison and a side-by-side table with Hold Total, Sell &amp; Invest, Year 1 Cash Flow, and Recommendation.</p>

<h2>Dashboard Analysis</h2>
<ul>
<li>Cash Flow composed chart, Equity Growth, Expense Pie, Sale Proceeds Waterfall, Year-by-Year Table</li>
</ul>

<h2>Share Link</h2>
<p>Click "Share Link" in the dashboard header to copy a compact URL that encodes your property inputs. Anyone who opens the link will see the same analysis. The link uses short keys and skips default values to keep URLs short.</p>

<h2>PDF Report</h2>
<p>Click "PDF Report" to open a print-ready report in a new tab. Includes property overview, key metrics, assumptions, capital expenses, year-by-year projections, AI summary (if generated), client profile (if discovery completed), and the full disclaimer. Click "Print / Save as PDF" to export.</p>

<h2>Pro Features</h2>
<p>Unlock via email verification. Pro status persists across sessions.</p>
<h3>Tax Benefits</h3>
<p>Straight-line vs cost segregation. Chart and labels match the hold period slider.</p>
<h3>Mortgage Comparison</h3>
<p>Two scenarios, three charts. Interest rate carries from the questionnaire.</p>
<h3>What-If Snapshots</h3>
<p>Save, delete, and compare up to 3 slider configurations side by side: grouped bar chart, radar overlay, 10-metric comparison table.</p>
<h3>AI Investment Summary</h3>
<p>Six presets with tone and length controls. Short-term rental industry benchmarks are built into the prompt (vacancy 40-55% is normal, cap rate 5-10%). Discovery profile, property condition, and capital expense data are all included. Markdown formatting renders as styled headers, bold, bullets, and numbered lists. Every summary ends with a liability disclaimer.</p>
<h3>Email AI Summary</h3>
<p>Pro users can email the AI summary to themselves via the "Email to Me" button.</p>

<h2>Buyer Mode</h2>
<p>When "I'm evaluating a new purchase" is selected in Goal Discovery, the entire app adapts:</p>
<ul>
<li><strong>Labels:</strong> Hold becomes "Buy &amp; Hold," Sell becomes "Don't Buy, Invest"</li>
<li><strong>Math:</strong> Purchase costs (default 3%) reduce buyer equity. "Don't Buy" invests the down payment plus closing costs at the alternative return rate. No selling costs, capital gains, or depreciation recapture.</li>
<li><strong>Questionnaire:</strong> hides Years Owned, Exit Strategy, and 1031. Relabels Mortgage to Loan.</li>
<li><strong>AI prompt:</strong> explicit buyer context — never references selling</li>
</ul>

<h2>Mobile Bottom Nav</h2>
<p>On screens under 640px, a fixed bottom navigation bar appears with icons for Overview, Analysis, Properties (if saved), AI (if Pro), and Help. Handles safe-area insets for phones with notches.</p>

<h2>How It Works Tab</h2>
<p>6-step walkthrough, key metrics glossary, and documentation.</p>

<h2>Installing as a Mobile App</h2>
<p>PropertyPath is a Progressive Web App. You can install it on your phone or tablet for a native app experience.</p>
<h3>Android (Chrome)</h3>
<ul><li>Open PropertyPath in Chrome</li><li>Tap the three-dot menu (top right)</li><li>Tap "Add to Home Screen" or "Install app"</li><li>The PropertyPath icon appears on your home screen</li></ul>
<h3>Android (Edge)</h3>
<ul><li>Open PropertyPath in Edge</li><li>Tap the hamburger menu</li><li>Tap "Add to phone" or "Install app"</li></ul>
<h3>iPhone / iPad (Safari)</h3>
<ul><li>Open PropertyPath in Safari</li><li>Tap the Share button (square with arrow)</li><li>Scroll down and tap "Add to Home Screen"</li><li>Tap "Add" to confirm</li></ul>
<p>Once installed, PropertyPath opens fullscreen without browser chrome. Your last-viewed analysis is available offline.</p>

<h2>Social Sharing</h2>
<p>When you share a PropertyPath link on social media or messaging apps, it displays a branded preview with the app name, description, and icon.</p>

<h2>Disclaimer</h2>
<div class="callout"><strong>⚠ Important:</strong> This tool does not constitute financial, tax, or investment advice. Projections are estimates based on user-provided inputs. Consult a qualified real estate professional, CPA, or financial advisor before making investment decisions. ${esc(b.name)}, its agents, and this platform assume no liability for decisions made based on this analysis.</div>
`;
  return docShell(b, 'User Manual', 'User', 'Manual', 'Complete guide to every field, tab, and feature', body);
}

// Term/definition pairs. Kept as data so the table renders from one loop and
// stays trivially editable.
const GLOSSARY = [
  ['Section', 'Core Concepts'],
  ['1031 Exchange', 'Tax-deferred property swap under IRS Section 1031.'],
  ['Adjusted Basis', 'Purchase price minus accumulated depreciation.'],
  ['Alternative Return', 'Expected annual return if you sell and invest elsewhere.'],
  ['Annual Appreciation', 'Expected yearly property value growth (adjustable in 0.5% steps).'],
  ['Cap Rate', 'Net operating income divided by property value. Calculated automatically in the Overview tab.'],
  ['Cash Flow', 'Revenue minus all expenses. Positive = profit, negative = loss.'],
  ['Equity', 'Property value minus mortgage balance.'],
  ['Hold Total Wealth', 'Equity plus cumulative net cash flow at the end of the hold period.'],
  ['Net Operating Income', 'Gross rent minus expenses and vacancy, before debt service.'],
  ['Total Wealth', 'Primary comparison: Hold (equity + cash) vs Sell (invested proceeds).'],
  ['Vacancy Rate', 'Percentage of time unoccupied. Short-term rental average is 40-55% — normal for that model.'],

  ['Section', 'Taxes & Depreciation'],
  ['Capital Gains Tax', 'Tax on profit from selling. Long-term rates: 0%, 15%, or 20%.'],
  ['Cost Segregation', 'IRS method to accelerate depreciation on building components.'],
  ['Depreciation', 'Tax deduction over 27.5 years on 85% of purchase price.'],
  ['Depreciation Recapture', 'The IRS recaptures claimed depreciation at 25% when you sell.'],

  ['Section', 'Costs'],
  ['Capital Expenses', 'Major replacement costs (roof, HVAC, water heater) projected from component ages and shown in the Upcoming Capital Expenses card.'],
  ['Maintenance Schedule', 'Roof replacement at 25 years (4% of value), HVAC at 15 years (2%), water heater at 12 years (0.5%). Overdue components trigger in Year 1.'],
  ['Purchase Costs', 'Buyer Mode equivalent of Selling Costs. Default 2-4% for closing costs, title, and inspections.'],
  ['Selling Costs', 'Agent commissions plus closing costs. Default 7.5%, adjustable for private sales (1-3%).'],

  ['Section', 'App Features'],
  ['Buyer Mode', 'Activated when "evaluating a new purchase" is selected. Changes labels to Buy & Hold vs Don\'t Buy Invest, adjusts math for closing costs, and hides exit strategy and 1031.'],
  ['Email My Results', 'Pro feature on the Overview tab. Sends a branded analysis email to the user.'],
  ['Goal Discovery', 'Five pre-analysis questions about situation, priority, risk, timeline, and experience that personalize your AI analysis.'],
  ['Mobile Bottom Nav', 'Fixed navigation bar on screens under 640px with icons for the main tabs.'],
  ['Onboarding Tour', '4-step guided walkthrough for first-time users. Dismisses permanently.'],
  ['PDF Report', 'Print-ready report with property details, metrics, projections, AI summary, and disclaimers. Opens in a new tab.'],
  ['Pro Persistence', 'Pro status is saved locally. It survives page refreshes and browser restarts.'],
  ['Progressive Web App', 'Technology that lets PropertyPath be installed on phones and tablets like a native app. Works offline for cached content.'],
  ['Saved Properties', 'Save up to 3 analyses for side-by-side comparison with charts and tables.'],
  ['Sensitivity Sliders', 'Vacancy, appreciation (0.5% steps), alternative return (0.5% steps), and hold period.'],
  ['Share Link', 'Compact URL encoding of property inputs for sharing. Uses short keys and skips defaults.'],
  ['What-If Snapshot', 'Saved slider assumptions. Compare up to 3 side by side.'],
];

export function buildGlossaryHTML() {
  const b = docBrand();
  let rows = '';
  let open = false;
  GLOSSARY.forEach(([term, def]) => {
    if (term === 'Section') {
      if (open) { rows += '</tbody></table>'; }
      rows += `<h2>${esc(def)}</h2><table><thead><tr><th>Term</th><th>Definition</th></tr></thead><tbody>`;
      open = true;
      return;
    }
    rows += `<tr><td>${esc(term)}</td><td>${esc(def)}</td></tr>`;
  });
  if (open) { rows += '</tbody></table>'; }
  return docShell(b, 'Glossary', 'Glossary', 'of Terms', 'Every metric and term used in PropertyPath', rows);
}
